'use client';

import { useEffect, useState } from 'react';
import RecentNews from '@/components/component_astro_crick/RecentNews';
import { VerticalSmallImagesNews } from '../component_vertical/VerticalSmallImagesNews';
import { VerticalMediumVideoNews } from '../component_vertical/VerticalMediumVideoNews';
import Email from '../component_form/email';
import SectionBottomAd from '../ads/SectionBottomAd';
import { API_PROXY_PATH } from '@/lib/constants';
import { getImageUrl, getTimeAgo } from '@/lib/componentutills';
interface ProcessedGalleryData {
  title: string;
  timeAgo: string;
  imageSrc?: string;
  slug: string;
  count: string;
  imageMetadata?: {
    'focus-point'?: [number, number];
    width?: number;
    height?: number;
  };
}
interface VideoData {
  title: string;
  timeAgo: string;
  imageSrc?: string;
  slug: string;
  imageMetadata?: {
    'focus-point'?: [number, number];
    width?: number;
    height?: number;
  };
}
interface ImageVideoProps {
  galleries?: ProcessedGalleryData[];
  videos?: VideoData[];
  /** Use 'article' for article pages, 'section' for other pages */
  adType?: 'article' | 'section';
}
export default function ImageVideo({
  galleries: propsGalleries,
  videos: propsVideos,
  adType = 'section'
}: ImageVideoProps) {
  const [galleries, setGalleries] = useState<ProcessedGalleryData[]>([]);
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [loading, setLoading] = useState(true);

  // Determine ad slot names based on adType
  const adSlot1 = adType === 'article' ? 'article-bottom1' : 'section-bottom1';
  const adSlot2 = adType === 'article' ? 'article-bottom' : 'section-bottom2';

  // Fetch data on mount - use props if they have data, otherwise fetch from API
  useEffect(() => {
    // Check if props have actual data (not just empty arrays)
    const hasGalleriesData = propsGalleries && propsGalleries.length > 0;
    const hasVideosData = propsVideos && propsVideos.length > 0;

    // If props have data, use them
    if (hasGalleriesData || hasVideosData) {
      setGalleries(propsGalleries || []);
      setVideos(propsVideos || []);
      setLoading(false);
      return;
    }

    // No props - fetch from API
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch galleries and videos using collections API
        const [galleriesRes, videosRes] = await Promise.all([fetch(`${API_PROXY_PATH}/collections/galleries`), fetch(`${API_PROXY_PATH}/collections/videos`)]);

        // Check if responses are OK and content-type is JSON
        let galleriesData: {
          items?: any[];
        } = {
          items: []
        };
        let videosData: {
          items?: any[];
        } = {
          items: []
        };
        if (galleriesRes.ok) {
          const contentType = galleriesRes.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            galleriesData = await galleriesRes.json();
          }
        }
        if (videosRes.ok) {
          const contentType_0 = videosRes.headers.get('content-type');
          if (contentType_0 && contentType_0.includes('application/json')) {
            videosData = await videosRes.json();
          }
        }

        // Process galleries - API returns { items: [{ story: {...} }] }
        const galleryItems = galleriesData?.items || [];
        const processedGalleries: ProcessedGalleryData[] = galleryItems.map((item: any) => item.story || item.item?.story).filter((story: any) => story && story.headline && story.slug && story['hero-image-s3-key']).slice(0, 4).map((story_0: any) => ({
          title: story_0.headline || '',
          timeAgo: getTimeAgo(story_0['last-published-at'] || story_0['published-at'] || Date.now()),
          imageSrc: getImageUrl(story_0['hero-image-s3-key']),
          slug: story_0.slug || '',
          count: String(story_0.cards?.length || 0),
          imageMetadata: story_0['hero-image-metadata']
        }));

        // Process videos - API returns { items: [{ story: {...} }] }
        const videoItems = videosData?.items || [];
        const processedVideos: VideoData[] = videoItems.map((item_0: any) => item_0.story || item_0.item?.story).filter((story_1: any) => story_1 && story_1.headline && story_1.slug && story_1['hero-image-s3-key']).slice(0, 4).map((story_2: any) => ({
          title: story_2.headline || '',
          timeAgo: getTimeAgo(story_2['last-published-at'] || story_2['published-at'] || Date.now()),
          imageSrc: getImageUrl(story_2['hero-image-s3-key']),
          slug: story_2.slug || '',
          imageMetadata: story_2['hero-image-metadata']
        }));
        setGalleries(processedGalleries);
        setVideos(processedVideos);
      } catch (error) {
        console.error('Failed to fetch galleries/videos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [propsGalleries, propsVideos]); // Re-run if props change

  const LoadingSkeleton = () => <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
      {[...Array(4)].map((_, i) => <div key={i} className="p-4 border-[0.1px] border-E8E8E8">
          <div className="animate-pulse">
            {/* aspect-[16/9] mirrors VerticalSmallImagesNews so the skeleton
                height scales with column width at every breakpoint, matching
                the rendered card exactly. */}
            <div className="w-full bg-gray-200 rounded mb-2 aspect-[16/9]"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>)}
    </div>;
  return <div>


      <div>
        <div className="border-[0.1px] border-E8E8E8">
          <RecentNews title={'புகைப்படங்கள்'} href="/galleries" />
        </div>

        {loading ? <LoadingSkeleton /> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {galleries.length === 0 && <div className="col-span-full flex flex-col items-center justify-center py-8 px-4">
                <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500 text-sm">புகைப்படங்கள் இல்லை</p>
              </div>}
            {galleries.map((gallery, index) => <div key={index} className="border-[0.1px] border-E8E8E8">
                <VerticalSmallImagesNews title={{
            value: gallery.title
          }} timeAgo={{
            value: gallery.timeAgo
          }} imageSrc={{
            value: gallery.imageSrc || ''
          }} slug={gallery.slug} count={gallery.count} imageMetadata={gallery.imageMetadata} />
              </div>)}
          </div>}
      </div>

      {/* Bottom Ad 1 */}
      <div className="py-4">
        <SectionBottomAd slot={adSlot1} />
      </div>

      <div className="">
        <div className="border-[0.1px] border-E8E8E8">
          <RecentNews title={'காணொலி'} href="/videos" />
        </div>

        {loading ? <LoadingSkeleton /> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {videos.length === 0 && <div className="col-span-full flex flex-col items-center justify-center py-8 px-4">
                <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500 text-sm">காணொலிகள் இல்லை</p>
              </div>}
            {videos.map((video, index_0) => <div key={index_0} className="border-[0.1px] border-E8E8E8">
                <VerticalMediumVideoNews title={{
            value: video.title
          }} timeAgo={{
            value: video.timeAgo
          }} imageSrc={{
            value: video.imageSrc || ''
          }} slug={video.slug} imageMetadata={video.imageMetadata} />
              </div>)}
          </div>}
      </div>
      {/* Bottom Ad 2 */}
      <div className="py-4">
        <SectionBottomAd slot={adSlot2} />
      </div>
    </div>;
}